import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { HtmlContent } from './html_content';
import { stripHtml } from './stripHtml';

/**
 * Renders content into Django-rendered page header elements
 * (breadcrumb, title, description) using React portals + HtmlContent.
 */
export const PageHeaderPortal = ({
  title,
  breadcrumbLabel,
  description,
  pageTitle,
  metaDescription,
  breadcrumbTitle,
  breadcrumbDescription,
}) => {
  const titleEl = useMemo(() => document.getElementById('dynamic-title'), []);
  const breadcrumbEl = useMemo(
    () => document.getElementById('dynamic-breadcrumb'),
    []
  );
  const breadcrumbContainer = useMemo(
    () => document.getElementById('dynamic-breadcrumb-container'),
    []
  );
  const descriptionEl = useMemo(
    () => document.getElementById('dynamic-description'),
    []
  );
  const descriptionContainer = useMemo(
    () => document.getElementById('dynamic-description-container'),
    []
  );

  // Replace skeleton title with i18n text
  useEffect(() => {
    const staticTitle = document.getElementById('static-title');
    if (staticTitle && breadcrumbTitle) {
      staticTitle.innerHTML = stripHtml(breadcrumbTitle);
    }
  }, [breadcrumbTitle]);

  // Replace skeleton description with i18n text
  useEffect(() => {
    const staticDesc = document.getElementById('static-description');
    if (staticDesc && breadcrumbDescription) {
      staticDesc.innerHTML = breadcrumbDescription;
      staticDesc.classList.remove('space-y-2');
    }
  }, [breadcrumbDescription]);

  useEffect(() => {
    if (breadcrumbContainer) {
      breadcrumbContainer.classList.toggle('hidden', !breadcrumbLabel);
      breadcrumbContainer.classList.toggle('flex', !!breadcrumbLabel);
    }
    return () => {
      if (breadcrumbContainer) {
        breadcrumbContainer.classList.add('hidden');
        breadcrumbContainer.classList.remove('flex');
      }
    };
  }, [breadcrumbLabel, breadcrumbContainer]);

  useEffect(() => {
    if (descriptionContainer) {
      descriptionContainer.classList.toggle('hidden', !description);
    }
    return () => {
      if (descriptionContainer) {
        descriptionContainer.classList.add('hidden');
      }
    };
  }, [description, descriptionContainer]);

  useEffect(() => {
    if (pageTitle) document.title = stripHtml(pageTitle);
    return () => {
      document.title = 'SAgE pesticides';
    };
  }, [pageTitle]);

  useEffect(() => {
    const meta = document.querySelector('meta[name="description"]');
    if (meta && metaDescription)
      meta.setAttribute('content', stripHtml(metaDescription));
    return () => {
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute('content', '');
    };
  }, [metaDescription]);

  return (
    <>
      {titleEl && createPortal(<HtmlContent html={title} />, titleEl)}
      {breadcrumbEl &&
        createPortal(<HtmlContent html={breadcrumbLabel} />, breadcrumbEl)}
      {descriptionEl &&
        description &&
        createPortal(<HtmlContent html={description} />, descriptionEl)}
    </>
  );
};

/**
 * Toggle dynamic breadcrumb visibility with plain text (for non-detail pages).
 */
export const updateDynamicBreadcrumb = (label) => {
  const breadcrumb = document.getElementById('dynamic-breadcrumb');
  const container = document.getElementById('dynamic-breadcrumb-container');

  if (breadcrumb) breadcrumb.textContent = label || '';
  if (container) {
    container.classList.toggle('hidden', !label);
    container.classList.toggle('flex', !!label);
  }
};
